import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ChoicesSelect from '../ChoicesSelect';
import useClickOutside from '../../hooks/useClickOutside';

interface WholesaleApplication {
  id: number;
  business_name: string;
  business_type: string;
  full_name: string;
  email: string;
  phone_number: string;
  country_region: string;
  city: string;
  state_province?: string;
  product_interests: string[];
  additional_information?: string;
  shipping_address: string;
  status: 'pending' | 'approved' | 'rejected' | 'under_review';
  admin_notes?: string;
  reviewed_at?: string;
  reviewer?: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

interface Props {
  application: WholesaleApplication;
  mode: 'view' | 'review';
  onClose: () => void;
}

const WholesaleApplicationModal: React.FC<Props> = ({ application, mode, onClose }) => {
  const modalRef = useClickOutside(onClose);
  const [status, setStatus] = useState(application.status);
  const [adminNotes, setAdminNotes] = useState(application.admin_notes || '');
  const [loading, setLoading] = useState(false);

  const handleStatusUpdate = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/admin/wholesale-applications/${application.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status,
          admin_notes: adminNotes,
        }),
      });

      if (response.ok) {
        onClose();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to update application status');
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Failed to update application status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      approved: 'text-green-600 bg-green-50 border-green-200',
      rejected: 'text-red-600 bg-red-50 border-red-200',
      under_review: 'text-blue-600 bg-blue-50 border-blue-200',
    };
    return colors[status as keyof typeof colors] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const formatProductInterests = (interests: string[]) => {
    return interests.map(interest => 
      interest.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    ).join(', ');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'view' ? 'Wholesale Application Details' : 'Review Application'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Application from {application.business_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Current Status:</span>
            <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(application.status)}`}>
              {application.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          {/* Business Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Business Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Name</label>
                <p className="mt-1 text-sm text-gray-900">{application.business_name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Business Type</label>
                <p className="mt-1 text-sm text-gray-900 capitalize">
                  {application.business_type.replace('_', ' ')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Product Interests</label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatProductInterests(application.product_interests)}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Contact Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <p className="mt-1 text-sm text-gray-900">{application.full_name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">
                  <a href={`mailto:${application.email}`} className="text-blue-600 hover:text-blue-800">
                    {application.email}
                  </a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                <p className="mt-1 text-sm text-gray-900">
                  <a href={`tel:${application.phone_number}`} className="text-blue-600 hover:text-blue-800">
                    {application.phone_number}
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Location Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Country/Region</label>
                <p className="mt-1 text-sm text-gray-900">{application.country_region}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <p className="mt-1 text-sm text-gray-900">{application.city}</p>
              </div>

              {application.state_province && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">State/Province</label>
                  <p className="mt-1 text-sm text-gray-900">{application.state_province}</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Shipping Address</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{application.shipping_address}</p>
            </div>
          </div>

          {/* Additional Information */}
          {application.additional_information && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Additional Information</h3>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{application.additional_information}</p>
            </div>
          )}

          {/* Application Timeline */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Application Timeline</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Applied On</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(application.created_at)}</p>
              </div>

              {application.reviewed_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Reviewed</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(application.reviewed_at)}</p>
                </div>
              )}
            </div>

            {application.reviewer && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Reviewed By</label>
                <p className="mt-1 text-sm text-gray-900">{application.reviewer.name}</p>
              </div>
            )}
          </div>

          {/* Admin Notes */}
          {mode === 'review' ? (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Review Application</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <ChoicesSelect
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'under_review', label: 'Under Review' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' }
                  ]}
                  value={status}
                  onChange={(value) => setStatus(value as any)}
                  placeholder="Select Status"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                  placeholder="Add notes about this application..."
                />
              </div>
            </div>
          ) : (
            application.admin_notes && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Admin Notes</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-md">
                  {application.admin_notes}
                </p>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {mode === 'review' ? 'Cancel' : 'Close'}
          </button>
          
          {mode === 'review' && (
            <button
              onClick={handleStatusUpdate}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Status'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WholesaleApplicationModal;
